import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'Net 15 Payment Terms — What It Means and When to Use It',
      'description': 'Net 15 means payment is due within 15 days of the invoice date. Learn how it compares to Net 30 and Due on Receipt, which industries use it, and how to enforce it.',
      'url': 'https://www.invilic.com/blog/net-15-payment-terms',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What does Net 15 mean on an invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Net 15 means the full invoice amount is due within 15 calendar days of the invoice date. If your invoice is dated June 1, payment is due by June 16. "Net" means the full (net) amount with no early payment discount. It is counted in calendar days, not business days, unless you specifically write "15 business days."' },
        },
        {
          '@type': 'Question',
          'name': 'Is Net 15 the same as 15 days net?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. "Net 15," "Net 15 days," "15 days net," "payment due within 15 days," and "N15" all mean the same thing — the full invoice amount is due within 15 calendar days of the invoice date. There is no difference between these phrasings.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between Net 15 and Net 30?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Net 15 gives the client 15 days to pay; Net 30 gives 30 days. Net 15 improves your cash flow by 15 days but can create friction with large corporate clients whose accounts payable systems run on 30-day cycles. Net 30 is the most widely accepted B2B standard. For most freelancers and small agencies, Net 15 is a good default for established clients.' },
        },
        {
          '@type': 'Question',
          'name': 'Is Net 15 standard for freelancers?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Net 15 is one of the most common payment terms for freelancers and independent contractors, alongside Net 30 and Due on Receipt. It balances the freelancer\'s need for cash flow with the client\'s need for a reasonable processing window. Many freelancers start with Net 30 for new clients and switch to Net 15 once a reliable payment relationship is established.' },
        },
        {
          '@type': 'Question',
          'name': 'Can I change from Net 30 to Net 15 for existing clients?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, but communicate the change before issuing the invoice, not after. Send a note with your next invoice — or when renewing a contract — explaining the updated terms. Clients who are used to Net 30 may push back, especially if they have monthly payment cycles. Most reasonable clients will accept Net 15 if given advance notice.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const comparisonData = [
  { term: 'Due on Receipt', days: '0–3 days', annualisedCost: 'Highest', bestFor: 'New clients, cash-critical projects', friction: 'High with corporates' },
  { term: 'Net 7', days: '7 days', annualisedCost: 'Very high', bestFor: 'Retainers, ongoing small jobs', friction: 'Moderate' },
  { term: 'Net 15', days: '15 days', annualisedCost: 'High', bestFor: 'Most freelancers', friction: 'Low to moderate' },
  { term: 'Net 30', days: '30 days', annualisedCost: 'Standard', bestFor: 'B2B standard', friction: 'Very low' },
  { term: 'Net 45 / 60', days: '45–60 days', annualisedCost: 'Low', bestFor: 'Enterprise/large buyers', friction: 'None' },
];

export default function Net15PaymentTermsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Net 15 Payment Terms — What It Means, vs Net 30, and When to Use It | Invilic Blog"
        description="Net 15 means payment due within 15 days of the invoice date. Covers what it means, Net 15 vs Net 30, which industries use it, and how to write it on your invoice."
        canonical="https://www.invilic.com/blog/net-15-payment-terms"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Basics</span>
          </div>
          <span className="inline-block bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Basics</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Net 15 Payment Terms — What It Means and When to Use It
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Net 15 means the full invoice amount is due within 15 calendar days of the invoice date. It is one of the most popular payment terms for freelancers and small businesses: faster than the industry-standard Net 30, but still reasonable enough for most clients to accept without friction.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Does Net 15 Mean?</h2>
          <p className="mb-4">
            "Net" in a payment term means the full amount — no early payment discount is involved. "15" is the number of calendar days from the invoice date. So Net 15 = pay the full invoice amount within 15 calendar days of the date on the invoice.
          </p>
          <p className="mb-4">
            If your invoice is dated <strong className="font-semibold text-gray-900 dark:text-white">June 1</strong>, payment is due by <strong className="font-semibold text-gray-900 dark:text-white">June 16</strong>. Weekends and public holidays count — it is calendar days, not business days. If you want business days, write "Net 15 business days" explicitly.
          </p>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-xl mb-6 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">All the same thing:</strong> "Net 15," "N/15," "Net 15 days," "15 days net," "payment due within 15 days" — these are all identical payment terms. Use whichever phrasing looks most professional on your invoice.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Net 15 vs Net 30 — Key Differences</h2>
          <p className="mb-5">The mechanics are simple; the real-world implications are more nuanced.</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800"></th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Net 15</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Net 30</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Days to pay', n15: '15 calendar days', n30: '30 calendar days' },
                  { label: 'Cash flow', n15: 'Better for you', n30: 'Better for client' },
                  { label: 'Corporate acceptance', n15: 'May require negotiation', n30: 'Widely accepted standard' },
                  { label: 'Freelancer suitability', n15: 'Ideal for most freelancers', n30: 'Better for large corporate clients' },
                  { label: 'Late payment risk', n15: 'Lower (shorter window)', n30: 'Slightly higher' },
                ].map(({ label, n15, n30 }) => (
                  <tr key={label} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">{label}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{n15}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{n30}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mb-8 text-sm">
            The practical impact: on a £3,000 invoice, switching from Net 30 to Net 15 means you receive funds two weeks earlier. Over a year with monthly invoicing, that is roughly £3,000 extra in working capital at any given time.
          </p>

          <BlogCTA
            text="Create a professional invoice with Net 15 payment terms already set. Free, no sign-up needed."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Payment Terms Comparison — Where Net 15 Sits</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Term</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Days</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Best For</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Client Friction</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map(({ term, days, bestFor, friction }) => (
                  <tr key={term} className={`border-b border-gray-100 dark:border-gray-800 ${term === 'Net 15' ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{term}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{days}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{bestFor}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{friction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Who Uses Net 15?</h2>
          <ul className="space-y-3 mb-8 text-sm">
            {[
              { who: 'Freelancers (design, writing, development)', why: 'Net 15 improves cash flow without alienating established clients' },
              { who: 'Marketing and creative agencies', why: 'Standard for project-based work with regular clients' },
              { who: 'Coaches and consultants', why: 'Session-based billing suits shorter payment windows' },
              { who: 'IT contractors', why: 'Monthly or project invoicing where Net 15 is a common contract term' },
              { who: 'Small professional service firms', why: 'Better cash flow than Net 30 without the urgency of Net 7' },
            ].map(({ who, why }) => (
              <li key={who} className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                <span><strong className="font-semibold text-gray-900 dark:text-white">{who}:</strong> {why}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">How to Write Net 15 on Your Invoice</h2>
          <p className="mb-4">Put it in the "Payment Terms" field. Any of these phrasings is clear and professional:</p>
          <div className="space-y-2 mb-8">
            {[
              '"Payment due within 15 days of invoice date (Net 15)"',
              '"Net 15 — payment due by [specific date]"',
              '"Due in 15 days"',
              '"Net 15. Interest of 1.5% per month on overdue balances."',
            ].map(ex => (
              <div key={ex} className="text-sm font-mono bg-gray-50 dark:bg-gray-900/30 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">
                {ex}
              </div>
            ))}
          </div>
          <p className="mb-8 text-sm">
            Adding the actual due date alongside the Net 15 term — "due by June 16" — removes any ambiguity and reduces late payments, because the client does not need to calculate the date themselves.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Set Net 15 or any payment terms on your invoice in Invilic. Professional PDF invoices, free forever."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/net-15-payment-terms" />
      </main>
    </div>
  );
}
